import React, { memo, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Icon, LoadingButton, Stack } from 'investira.react.components';
import { PageTemplate } from '../../../components/templates';
import { useAvaliacoesContext } from '../../../contexts/AvaliacoesContext';
import { useEntidadeAvaliacaoContext } from '../../../contexts/EntidadeAvaliacaoContext';
import withMessage from '../../../hoc/withMessage';
import { getAvaliacaoStatusId } from '../_shared/avaliacaoStatus';
import AvaliacoesGestorEditGateView from './AvaliacoesGestorEditGateView';
import AvaliacoesGestorEditLoadingView from './AvaliacoesGestorEditLoadingView';
import AvaliacoesGestorEditOtpView from './AvaliacoesGestorEditOtpView';
import AvaliacoesGestorEditQuestionarioView from './AvaliacoesGestorEditQuestionarioView';
import AvaliacoesGestorAnexoRemocaoDialog from './AvaliacoesGestorAnexoRemocaoDialog';
import AvaliacoesGestorEntregaDialog from './AvaliacoesGestorEntregaDialog';
import {
  wFluxoEntradaAvaliacao,
  wOtpGestorEtapas,
  wFluxoEntradaStatusEncerrada,
  wFluxoEntradaStatusEntregue
} from './avaliacoesGestorEdit.constants';
import useAvaliacoesGestorEditController from './useAvaliacoesGestorEditController';

const AvaliacoesGestorEdit = memo(props => {
  const { state, actions } = useAvaliacoesContext();
  const { state: entidadeAvaliacaoState, actions: entidadeAvaliacaoActions } =
    useEntidadeAvaliacaoContext();
  const params = useParams();
  const location = useLocation();
  const xNavigate = useNavigate();

  /** Remove o OTP validado da URL sem alterar os demais dados de navegação. */
  const pvRemoverOtpDaUrl = useCallback(() => {
    const xSearchParams = new URLSearchParams(location?.search || '');

    if (!xSearchParams.has('otp')) {
      return;
    }

    xSearchParams.delete('otp');
    const xSearch = xSearchParams.toString();

    xNavigate(
      {
        pathname: location?.pathname,
        search: xSearch ? `?${xSearch}` : '',
        hash: location?.hash
      },
      { replace: true }
    );
  }, [location?.hash, location?.pathname, location?.search, xNavigate]);
  const xController = useAvaliacoesGestorEditController({
    paramsId: params?.id,
    locationSearch: location?.search,
    state,
    actions,
    contextAvaliacao: entidadeAvaliacaoState?.avaliacao,
    hasResolvedAvaliacaoStatus: entidadeAvaliacaoState?.hasResolvedAvaliacaoStatus,
    isLoadingAvaliacaoStatus: entidadeAvaliacaoState?.isLoadingAvaliacaoStatus,
    onRefreshAvaliacaoStatus: entidadeAvaliacaoActions.readAvaliacaoStatus,
    onOtpValidated: pvRemoverOtpDaUrl,
    onMessageError: props.onMessageError,
    onMessageSuccess: props.onMessageSuccess
  });
  const xStatusContextoBruto = entidadeAvaliacaoState?.avaliacao?.status;
  const xStatusContextoId = (() => {
    const xStatusNormalizado = getAvaliacaoStatusId(xStatusContextoBruto);

    if (xStatusNormalizado !== null) {
      return xStatusNormalizado;
    }

    const xStatusNumerico = Number(xStatusContextoBruto);

    return Number.isFinite(xStatusNumerico) ? xStatusNumerico : null;
  })();
  const xGateDesabilitado = xController.flow.acaoEmAndamento || xController.flow.telaCarregando;
  const xAvaliacaoTokenPreenchido = Boolean(xController.avaliacaoToken);
  const xDeveExibirOtp =
    xController.flow.otpNecessario ||
    (xController.flow.entrada === wFluxoEntradaAvaliacao.gate && !xAvaliacaoTokenPreenchido);
  const xGateMode = (() => {
    if (xController.flow.entrada === wFluxoEntradaAvaliacao.entregue) {
      return 'entregue';
    }

    if (xController.flow.entrada === wFluxoEntradaAvaliacao.encerrada) {
      return 'encerrada';
    }

    if (
      xController.flow.entrada === wFluxoEntradaAvaliacao.gate &&
      !xDeveExibirOtp &&
      xStatusContextoId !== null &&
      xStatusContextoId < 10
    ) {
      return 'confirmacao';
    }

    if (
      wFluxoEntradaStatusEncerrada.has(xStatusContextoId) ||
      (xStatusContextoId !== null && xStatusContextoId >= 40)
    ) {
      return 'encerrada';
    }

    if (wFluxoEntradaStatusEntregue.has(xStatusContextoId)) {
      return 'entregue';
    }

    return null;
  })();
  const xOtp = xDeveExibirOtp
    ? {
        ...xController.otp,
        status: {
          ...xController.otp.status,
          etapa: xController.otp.status.etapa || wOtpGestorEtapas.solicitar
        }
      }
    : xController.otp;

  const pvRenderHeaderActions = () => {
    if (
      xController.flow.entrada !== wFluxoEntradaAvaliacao.questionario ||
      xGateMode ||
      xDeveExibirOtp
    ) {
      return undefined;
    }

    return (
      <Stack direction="row" spacing={2} alignItems="center">
        {/* <LoadingButton
          variant="outlined"
          color="primary"
          startIcon={<Icon iconName="upload" size={16} />}
          size="small"
          sx={{ px: 2 }}
          type="submit"
          disabled={props.disabledButtons}>
          Salvar Rascunho
        </LoadingButton> */}
        <LoadingButton
          variant="contained"
          color="primary"
          startIcon={<Icon iconName="paper_plane" size={16} />}
          size="small"
          sx={{ px: 2 }}
          data-testid="abrir-assinatura-entrega"
          onClick={xController.actions.handleOpenAssinaturaDialog}
          disabled={
            !xController.questionario.todasRespondidas ||
            props.disabledButtons ||
            xController.flow.acaoEmAndamento
          }>
          Entregar
        </LoadingButton>
      </Stack>
    );
  };

  const pvHandleAceitarQuestionario = () => {
    xController.actions.handleAceitarQuestionario({
      onSuccess: () => entidadeAvaliacaoActions.readAvaliacaoStatus(params?.id)
    });
  };

  const pvHandleRecusarQuestionario = () => {
    xController.actions.handleRecusarQuestionario({
      onSuccess: () => entidadeAvaliacaoActions.readAvaliacaoStatus(params?.id)
    });
  };

  const pvRenderContent = () => {
    if (xController.flow.telaCarregando) {
      return <AvaliacoesGestorEditLoadingView />;
    }

    if (xGateMode) {
      return (
        <AvaliacoesGestorEditGateView
          mode={xGateMode}
          disabled={xGateDesabilitado}
          onAceitar={pvHandleAceitarQuestionario}
          onRecusar={pvHandleRecusarQuestionario}
        />
      );
    }

    if (xDeveExibirOtp) {
      return (
        <AvaliacoesGestorEditOtpView
          otp={xOtp}
          onSolicitarOtp={xController.actions.handleSolicitarOtp}
          onSubmitOtp={xController.actions.handleSubmitOtp}
          onChangeOtp={xController.actions.handleChangeOtp}
          onKeyDownOtp={xController.actions.handleKeyDownOtp}
          onPasteOtp={xController.actions.handlePasteOtp}
          onReenviarOtp={xController.actions.handleReenviarOtp}
          onClearOtp={xController.actions.handleClearOtp}
        />
      );
    }

    return (
      <>
        <AvaliacoesGestorEditQuestionarioView
          questionario={xController.questionario}
          anexos={xController.anexos}
          actions={xController.actions}
          disabledButtons={props.disabledButtons}
          fluxoAcaoEmAndamento={xController.flow.acaoEmAndamento}
        />
        <AvaliacoesGestorEntregaDialog
          open={xController.dialogs.assinaturaOpen}
          confirmada={xController.dialogs.assinaturaConfirmada}
          statusEntrega={xController.dialogs.assinaturaEntregaStatus}
          erroEntrega={xController.dialogs.assinaturaEntregaErro}
          isEntregando={xController.dialogs.assinaturaEntregando}
          disabled={xController.flow.acaoEmAndamento}
          onClose={xController.actions.handleCloseAssinaturaDialog}
          onChangeConfirmacao={xController.actions.handleChangeConfirmacaoEntrega}
          onConfirm={xController.actions.handleFinalizarEntrega}
        />
        <AvaliacoesGestorAnexoRemocaoDialog
          anexo={xController.dialogs.anexoRemocaoPendente}
          onClose={xController.actions.handleCloseRemoveAnexoDialog}
          onConfirm={xController.actions.handleRemoveAnexo}
        />
      </>
    );
  };

  const xPageMaxWidth =
    xController.flow.entrada === wFluxoEntradaAvaliacao.questionario
      ? xDeveExibirOtp
        ? 'sm'
        : 'xl'
      : xGateMode === 'entregue' || xGateMode === 'encerrada'
      ? 'md'
      : 'sm';
  const xPageTitle =
    xController.page.title ||
    entidadeAvaliacaoState?.avaliacao?.gestor?.pessoa_fantasia ||
    'Questionário';

  return (
    <PageTemplate
      title={xPageTitle}
      subtitle={xController.page.subtitle}
      description={xController.page.description}
      maxWidth={xPageMaxWidth}
      breadcrumbs={false}
      headerRenderActions={pvRenderHeaderActions()}
      footerProps={{
        variant: 'avaliacao'
      }}>
      {pvRenderContent()}
    </PageTemplate>
  );
});

AvaliacoesGestorEdit.displayName = 'AvaliacoesGestorEdit';

export default withMessage(AvaliacoesGestorEdit);
